import React, {useState} from 'react'
import {observer} from 'mobx-react'
import {useForm} from 'react-hook-form'
import {ExclamationCircleIcon, LocationIcon} from '../../../components/icons'
import LoadingOverlay from '../../../components/loading-overlay'
import useDeliveryBooking from '../../../hooks/use-delivery-booking'
import {useBasketStore, useCustomerStore} from '../../../store/hooks/useStore'
import {Box, Flex, Stack, Text, useDisclosure} from '../../../vanilla'
import {EditPostCode} from './edit-post-code'
import {YourDeliverySavedAddresses} from './your-delivery-saved-addresses'
import {YourDeliverySelectedAddress} from './your-delivery-selected-address'
import {EditAddressForm} from './edit-address-form'
import {AddAddressForm} from './add-address-form'
import * as styles from '../styles.css'
import {ChangingAddressWarningModal} from './changing-address-warning-modal'
import {noop} from '../../../utils/utils'
import {OrderAddress} from '../../../types/store/basket'
import {CheckPostalCodeFormFields} from '../../../types/forms'
import {useSoftLogoutData} from '../../../hooks/use-soft-logout-data'

export const YourDelivery = observer(() => {
  const {
    deliveryPostalCode,
    basket,
    addressDisplay,
    updateBasketDeliveryNote,
    shippingAddress,
    currentDeliverySlotId,
    isAppliedPostCodeDeliverable,
    deliveryAddressFormExpanded,
    expandDeliveryAddressForm
  } = useBasketStore()
  const {customerInfo, hasSavedAddresses, addresses} = useCustomerStore()
  const softLogoutData = useSoftLogoutData()

  const [deliveryNotesLoading, setDeliveryNotesLoading] = useState<boolean>(false)
  const forbiddenCharsRegex = /[()&"'<>\r\n]/g

  const form = useForm<CheckPostalCodeFormFields>({
    defaultValues: {
      postcode: deliveryPostalCode || '',
    },
  })

  const {
    setPostalCode,
    setDeliveryAddress,
    checkDeliveryValidity,
    loading,
    postCodeAvailability,
    setPostCodeAvailability
  } = useDeliveryBooking()

  const changingDeliveryAddressWarningModal = useDisclosure()
  const [changingDeliveryModalSubmitAction, setChangingDeliveryModalAction] = useState<
    (() => void) | null
  >(null)

  const customerHasBookedSlot = Boolean(currentDeliverySlotId)

  const handleAddNewDeliveryAddress = () => {
    if (customerHasBookedSlot && isAppliedPostCodeDeliverable) {
      setChangingDeliveryModalAction(() => {
        return () => expandDeliveryAddressForm('add')
      })
      changingDeliveryAddressWarningModal.onOpen()
    } else {
      expandDeliveryAddressForm('add')
    }
  }

  const handleEditDeliveryAddress = () => {
    if (customerHasBookedSlot && isAppliedPostCodeDeliverable) {
      setChangingDeliveryModalAction(() => {
        return () => expandDeliveryAddressForm('edit')
      })
      changingDeliveryAddressWarningModal.onOpen()
    } else {
      expandDeliveryAddressForm('edit')
    }
  }

  const handleSetDeliveryAddress = async (address: OrderAddress) => {
    if (customerHasBookedSlot && isAppliedPostCodeDeliverable) {
      setChangingDeliveryModalAction(() => {
        return () => setDeliveryAddress(address)
      })
      changingDeliveryAddressWarningModal.onOpen()
    } else {
      await setDeliveryAddress(address)
    }
  }

  const customerAddressSelected = addresses.find(
    (address) => address.addressId === shippingAddress?.c_customerAddressID,
  )

  const handleDeliveryNoteInput = (event: React.FormEvent<HTMLTextAreaElement>) => {
    let note = event.currentTarget.value
    if (forbiddenCharsRegex.test(note)) {
      note = note.replace(forbiddenCharsRegex, '')
      event.currentTarget.value = note
    }
  }

  return (
    <Box
      position="relative"
      backgroundColor="white"
      gridColumn={['1', '1', 'span 7']}
    >
      <LoadingOverlay isLoading={loading || !customerInfo} />
      <Text
        variant="heading1"
        paddingX="28px"
        paddingY="16px"
        borderBottom="1px"
        borderColor="gray200"
      >
        {deliveryPostalCode && !isAppliedPostCodeDeliverable
          ? 'Sorry, your address is not in coverage!'
          : 'Your delivery'}
      </Text>

      <Stack paddingX="28px" paddingTop="16px" paddingBottom="28px" spacing="28px">
        {!isAppliedPostCodeDeliverable && deliveryPostalCode && (
          <>
            <Stack spacing="16px">
              <Text variant="text4">
                {
                  "Unfortunately, we're not able to offer delivery or collection for your chosen address:"
                }
              </Text>
              <Flex align="center" gap="4px">
                <ExclamationCircleIcon />

                <Text variant="text4" style={{color: '#9b9b9b'}} data-cs-mask="">
                  {addressDisplay}
                </Text>
              </Flex>
            </Stack>
          </>
        )}

        {softLogoutData.softLogout &&  (
          <Flex gap="8px">
            <LocationIcon boxSize="20px" style={{color: '#bebebe'}} />
            <Stack spacing="4px" data-cs-mask="">
              {softLogoutData.firstName && (
                <Text variant="text4">{softLogoutData.firstName}</Text>
              )}
              <Text variant="text4">
                {[softLogoutData.email, softLogoutData.postcode].filter(Boolean).join(', ')}
              </Text>
            </Stack>
          </Flex>
        )}

        {!softLogoutData.softLogout && (
          <>
            {hasSavedAddresses && (
              <YourDeliverySavedAddresses
                delimiter={!isAppliedPostCodeDeliverable && deliveryPostalCode}
                setDeliveryAddress={handleSetDeliveryAddress}
                onAddNewAddressClick={handleAddNewDeliveryAddress}
              />
            )}
            {customerAddressSelected && (
              <YourDeliverySelectedAddress
                onEditAddressClick={handleEditDeliveryAddress}
                onNewAddressClick={handleAddNewDeliveryAddress}
                selectedAddress={customerAddressSelected}
              />
            )}
            {!customerAddressSelected && (
              <EditPostCode
                form={form}
                setPostalCode={setPostalCode}
                checkDeliveryValidity={checkDeliveryValidity}
                postCodeAvailability={postCodeAvailability}
                setPostCodeAvailability={setPostCodeAvailability}
              />
            )}
          </>
        )}
      </Stack>
      {deliveryAddressFormExpanded ? (
        <>
          {deliveryAddressFormExpanded === 'add' && (
            <AddAddressForm
              setDeliveryAddress={setDeliveryAddress}
              cleanupFunction={() => expandDeliveryAddressForm(null)}
            />
          )}
          {deliveryAddressFormExpanded === 'edit' && (
            <EditAddressForm
              setDeliveryAddress={setDeliveryAddress}
              cleanupFunction={() => expandDeliveryAddressForm(null)}
            />
          )}
        </>
      ) : null}
      <Stack
        paddingBottom="16px"
        paddingX="28px"
        spacing="4px"
        className={styles.deliveryNotesContainer}
        position="relative"
      >
        <LoadingOverlay isLoading={deliveryNotesLoading} />
        <Text variant="text3">Delivery notes (Optional)</Text>
        <Box
          as="textarea"
          placeholder="Important notes e.g. information about your delivery..."
          maxLength={100}
          padding="8px"
          borderColor="gray200"
          borderRadius="medium"
          fontFamily="body"
          fontSize="sm"
          className={styles.deliveryNotes}
          onInput={handleDeliveryNoteInput}
          onBlur={async (event: React.FocusEvent<HTMLTextAreaElement>) => {
            const note = event.target.value
            if (note) {
              try {
                setDeliveryNotesLoading(true)
                await updateBasketDeliveryNote(note)
                setDeliveryNotesLoading(false)
              } catch (error) {
                console.error('Error setting delivery notes', error)
                setDeliveryNotesLoading(false)
              }
            }
          }}
          disabled={!basket}
        />
      </Stack>
      <ChangingAddressWarningModal
        onSubmit={changingDeliveryModalSubmitAction ?? noop}
        {...changingDeliveryAddressWarningModal}
      />
    </Box>
  )
})
